/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  VideoLibrary,
  Image,
  PictureAsPdf,
  Description,
  InsertDriveFile,
  GridOn,
  Notes,
  Audiotrack,
  Archive,
  CloudUpload,
  CloudDone,
  CloudOff,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Speed,
  CheckCircle,
  Error as ErrorIcon,
  Pause,
  PlayArrow,
  Close,
  CloudDownload,
} from '@material-ui/icons';
import { CircularProgress } from '@material-ui/core';
import uploadManager, { UPLOAD_STATUS } from '../../Utils/uploadManagerNew';
import { AppContext } from '../../Context';
import styles from './UploadPanel.module.css';

const UploadPanel = () => {
  const [uploads, setUploads] = useState([]);
  const [networkStatus, setNetworkStatus] = useState('online');
  const [isMinimized, setIsMinimized] = useState(false);
  const [hoveringItems, setHoveringItems] = useState(new Set());
  const {
    downloadingFiles,
    showDownloadBox,
    setDownloadingFiles,
    pausedDownloadsRef,
    handleCancelAllDownloads,
    handleCancelDownload,
    handlePauseDownload,
    handleResumeDownload,
  } = useContext(AppContext);

  useEffect(() => {
    // Subscribe to upload manager updates
    const unsubscribe = uploadManager.subscribe(({ uploads: newUploads, networkStatus: status }) => {
      setUploads(newUploads);
      setNetworkStatus(status);
    });

    return unsubscribe;
  }, []);

  // Auto-expand when there's NEW activity (downloading or uploading starts)
  useEffect(() => {
    const hasActiveUploads = uploads.some(
      (u) => u.status === UPLOAD_STATUS.UPLOADING
        || u.status === UPLOAD_STATUS.PENDING
        || u.status === UPLOAD_STATUS.INITIALIZING
    );
    const hasActiveDownloads = showDownloadBox && downloadingFiles && downloadingFiles.length > 0
      && downloadingFiles.some((f) => f.status === 'downloading');

    const hasActivity = hasActiveUploads || hasActiveDownloads;

    // Only auto-expand when activity starts (not already expanded)
    if (hasActivity) {
      setIsMinimized(false);
    }
  }, [uploads.length, downloadingFiles?.length, showDownloadBox]);

  // Remove completed downloads after they finish
  useEffect(() => {
    if (downloadingFiles && downloadingFiles.length > 0) {
      const allCompleted = downloadingFiles.every((file) => file.status === 'completed' || file.status === 'error');
      const hasCompleted = downloadingFiles.some((file) => file.status === 'completed');

      if (allCompleted && hasCompleted) {
        // Wait 2 seconds before removing completed downloads so user can see completion
        const timer = setTimeout(() => {
          setDownloadingFiles([]);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [downloadingFiles, setDownloadingFiles]);

  const getFileIcon = useCallback((fileName, uploadType) => {
    // For photo albums, show stacked images icon
    if (uploadType === 'photo') {
      return (
        <div style={{ position: 'relative', width: 24, height: 24 }}>
          <Image
            style={{
              fontSize: 20,
              color: '#4CAF50',
              position: 'absolute',
              top: 5,
              left: 5,
              opacity: 0.4
            }}
          />
          <Image
            style={{
              fontSize: 20,
              color: '#4CAF50',
              position: 'absolute',
              top: 2,
              left: 2,
              opacity: 0.7
            }}
          />
          <Image
            style={{
              fontSize: 20,
              color: '#4CAF50',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </div>
      );
    }

    const ext = fileName?.split('.').pop()?.toLowerCase() || '';

    const iconProps = { style: { fontSize: 24 } };

    switch (ext) {
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
      case 'webm':
        return <VideoLibrary {...iconProps} style={{ ...iconProps.style, color: '#2196F3' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image {...iconProps} style={{ ...iconProps.style, color: '#4CAF50' }} />;
      case 'pdf':
        return <PictureAsPdf {...iconProps} style={{ ...iconProps.style, color: '#F44336' }} />;
      case 'doc':
      case 'docx':
        return <Description {...iconProps} style={{ ...iconProps.style, color: '#3F51B5' }} />;
      case 'xls':
      case 'xlsx':
        return <GridOn {...iconProps} style={{ ...iconProps.style, color: '#009688' }} />;
      case 'txt':
        return <Notes {...iconProps} style={{ ...iconProps.style, color: '#9E9E9E' }} />;
      case 'mp3':
      case 'wav':
        return <Audiotrack {...iconProps} style={{ ...iconProps.style, color: '#9C27B0' }} />;
      case 'zip':
      case 'rar':
        return <Archive {...iconProps} style={{ ...iconProps.style, color: '#FF9800' }} />;
      default:
        return <InsertDriveFile {...iconProps} style={{ ...iconProps.style, color: '#757575' }} />;
    }
  }, []);

  const formatBytes = useCallback((bytes) => {
    if (bytes <= 0) return '0 B';
    const suffixes = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let doubleBytes = bytes;
    while (doubleBytes >= 1024 && i < suffixes.length - 1) {
      doubleBytes /= 1024;
      i += 1;
    }
    return `${doubleBytes.toFixed(1)} ${suffixes[i]}`;
  }, []);

  const formatTime = useCallback((seconds) => {
    if (!seconds || seconds <= 0 || !Number.isFinite(seconds)) return '--:--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }, []);

  const formatSpeed = useCallback((bytesPerSecond) => {
    if (!bytesPerSecond || bytesPerSecond <= 0) return '-- MB/s';
    return `${formatBytes(bytesPerSecond)}/s`;
  }, [formatBytes]);

  const handlePauseResume = useCallback((uploadId, status) => {
    if (status === UPLOAD_STATUS.UPLOADING) {
      uploadManager.pauseUpload(uploadId);
    } else if (status === UPLOAD_STATUS.PAUSED || status === UPLOAD_STATUS.NETWORK_ERROR) {
      uploadManager.resumeUpload(uploadId);
    }
  }, []);

  const handleCancel = useCallback((uploadId) => {
    uploadManager.cancelUpload(uploadId);
  }, []);

  const handleCancelAll = useCallback(() => {
    const inProgressUploads = uploads.filter(
      (u) => u.status === UPLOAD_STATUS.UPLOADING
        || u.status === UPLOAD_STATUS.PAUSED
        || u.status === UPLOAD_STATUS.NETWORK_ERROR
        || u.status === UPLOAD_STATUS.PENDING
        || u.status === UPLOAD_STATUS.INITIALIZING
    );

    const inProgressDownloads = downloadingFiles.filter(
      (f) => f.status === 'downloading' || f.status === 'paused'
    );

    if (inProgressUploads.length > 0) {
      inProgressUploads.forEach((upload) => {
        uploadManager.cancelUpload(upload.id);
      });
    }

    if (inProgressDownloads.length > 0 && handleCancelAllDownloads) {
      handleCancelAllDownloads();
    }

    if (inProgressUploads.length === 0 && inProgressDownloads.length === 0) {
      // Clear completed uploads
      uploads.forEach((upload) => {
        if (upload.status === UPLOAD_STATUS.COMPLETED) {
          uploadManager.removeUpload(upload.id);
        }
      });
      // Clear completed downloads
      setDownloadingFiles([]);
    }
  }, [uploads, downloadingFiles, handleCancelAllDownloads, setDownloadingFiles]);

  const handleMouseEnter = useCallback((uploadId) => {
    setHoveringItems((prev) => new Set(prev).add(uploadId));
  }, []);

  const handleMouseLeave = useCallback((uploadId) => {
    setHoveringItems((prev) => {
      const next = new Set(prev);
      next.delete(uploadId);
      return next;
    });
  }, []);

  const inProgressUploads = uploads.filter(
    (u) => u.status === UPLOAD_STATUS.UPLOADING
      || u.status === UPLOAD_STATUS.PAUSED
      || u.status === UPLOAD_STATUS.NETWORK_ERROR
      || u.status === UPLOAD_STATUS.INITIALIZING
  );
  const pendingUploads = uploads.filter((u) => u.status === UPLOAD_STATUS.PENDING);
  const completedUploads = uploads.filter((u) => u.status === UPLOAD_STATUS.COMPLETED);
  const hasActiveUploads = inProgressUploads.length > 0 || pendingUploads.length > 0;

  // Calculate total speed
  const totalSpeed = inProgressUploads
    .filter((u) => u.status === UPLOAD_STATUS.UPLOADING)
    .reduce((sum, upload) => sum + (upload.speed || 0), 0);

  // Check if there are active downloads
  const hasDownloads = showDownloadBox && downloadingFiles && downloadingFiles.length > 0;
  const activeDownloads = hasDownloads ? downloadingFiles.filter((f) => f.status === 'downloading') : [];
  const hasActiveDownloads = activeDownloads.length > 0;

  if (uploads.length === 0 && !hasDownloads) return null;

  return (
    <div className={styles.uploadPanel}>
      <div
        className={styles.header}
        onDoubleClick={() => setIsMinimized(!isMinimized)}
      >
        <div className={styles.headerContent}>
          {uploads.length === 0 && !hasDownloads ? (
            <CloudOff className={styles.headerIcon} style={{ color: '#9E9E9E' }} />
          ) : hasActiveDownloads && !hasActiveUploads ? (
            <CloudDownload className={styles.headerIcon} style={{ color: '#5aa5ef' }} />
          ) : hasActiveUploads ? (
            <CloudUpload className={styles.headerIcon} style={{ color: '#5aa5ef' }} />
          ) : (
            <CloudDone className={styles.headerIcon} style={{ color: '#5aa5ef' }} />
          )}
          <div className={styles.headerText}>
            <div className={styles.headerTitle}>
              {/* {uploads.length === 0 && !hasDownloads
                ? 'No active uploads'
                : hasActiveDownloads && !hasActiveUploads
                ? `Downloading ${activeDownloads.length} file${activeDownloads.length > 1 ? 's' : ''}`
                : hasActiveUploads
                ? `Uploading ${inProgressUploads.length} file${inProgressUploads.length > 1 ? 's' : ''}`
                : `Uploaded ${completedUploads.length} file${completedUploads.length > 1 ? 's' : ''}`} */}
              {
                uploads.length === 0 && !hasDownloads
                  ? '' // nothing happening
                  : hasActiveDownloads && !hasActiveUploads
                    ? `Downloading ${activeDownloads.length} file${activeDownloads.length > 1 ? 's' : ''}`
                    : hasActiveUploads
                      ? `Uploading ${inProgressUploads.length} file${inProgressUploads.length > 1 ? 's' : ''}`
                      : completedUploads.length > 0
                        ? `Uploaded ${completedUploads.length} file${completedUploads.length > 1 ? 's' : ''}`
                        : ''
              }
            </div>
            {(uploads.length > 0 || hasDownloads) && (hasActiveUploads || hasActiveDownloads) && (
              <div className={styles.headerSubtitle}>
                {hasActiveDownloads && `${activeDownloads.length} downloading`}
                {hasActiveDownloads && hasActiveUploads && ' • '}
                {pendingUploads.length > 0 && `${pendingUploads.length} in queue`}
                {(hasActiveDownloads || pendingUploads.length > 0) && completedUploads.length > 0 && ' • '}
                {completedUploads.length > 0 && `${completedUploads.length} completed`}
              </div>
            )}
            {uploads.length === 0 && !hasDownloads && (
              <div className={styles.headerSubtitle}>
                Select files to start uploading
              </div>
            )}
          </div>
          <button
            className={styles.minimizeButton}
            onClick={() => setIsMinimized(!isMinimized)}
            type="button"
          >
            {isMinimized ? (
              <KeyboardArrowUp style={{ fontSize: 20 }} />
            ) : (
              <KeyboardArrowDown style={{ fontSize: 20 }} />
            )}
          </button>
        </div>
      </div>

      {!isMinimized && (uploads.length > 0 || hasDownloads) && (
        <>
          <div className={styles.statsBar}>
            <div className={styles.statsLeft}>
              {hasActiveUploads && (
                <>
                  <Speed style={{ fontSize: 14, color: '#757575' }} />
                  <span className={styles.statsText}>{formatSpeed(totalSpeed)}</span>
                </>
              )}
            </div>
            <button
              className={styles.clearAllButton}
              onClick={handleCancelAll}
              type="button"
            >
              {hasActiveUploads ? 'Cancel all' : 'Clear all'}
            </button>
          </div>

          <div className={styles.uploadList}>
            {/* Downloading Section */}
            {hasDownloads && (
              <>
                {downloadingFiles.some((f) => f.source !== 'bulk') && (
                  <div className={styles.sectionDivider}>
                    <CloudDownload style={{ fontSize: 16, color: '#757575', marginRight: 8 }} />
                    <span className={styles.sectionTitle}>Downloading from Cloud</span>
                  </div>
                )}
                {downloadingFiles.map((file) => {
                  // Check both state and ref for accurate pause status
                  const isActuallyPaused = pausedDownloadsRef.current.has(file.id);
                  const isDownloading = file.status === 'downloading' && !isActuallyPaused;
                  const isPaused = file.status === 'paused' || isActuallyPaused;
                  const isCompleted = file.status === 'completed';
                  const isError = file.status === 'error';
                  const isHovering = hoveringItems.has(file.id);

                  return (
                    <div
                      key={file.id}
                      className={`${styles.uploadItem} ${isError ? styles.uploadItemError : ''} ${isHovering ? styles.uploadItemHover : ''}`}
                      onMouseEnter={() => handleMouseEnter(file.id)}
                      onMouseLeave={() => handleMouseLeave(file.id)}
                    >
                      <div className={styles.uploadItemContent}>
                        <div className={styles.fileIcon}>
                          {getFileIcon(file.fileName || file.name, file.type)}
                        </div>
                        <div className={styles.uploadInfo}>
                          <div className={styles.fileName}>{file.name}</div>
                          <div className={styles.uploadMeta}>
                            {isPaused && (
                              <span className={styles.statusTextPaused}>
                                Paused - Click play to resume
                              </span>
                            )}
                            {isError && (
                              <span className={styles.statusTextError}>Download failed</span>
                            )}
                            {!isPaused && !isError && (
                              <>
                                {file.type === 'photo' && file.totalPhotos ? (
                                  <span className={styles.metaText}>
                                    {Math.round(((file.progress || 0) * file.totalPhotos) / 100)} / {file.totalPhotos} photos
                                  </span>
                                ) : (
                                  <span className={styles.metaText}>{file.progress}% downloaded</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className={styles.progressIndicator}>
                          {isCompleted ? (
                            <div className={styles.completedIcon}>
                              <CheckCircle style={{ fontSize: 24, color: '#4CAF50' }} />
                            </div>
                          ) : isError ? (
                            <div className={styles.errorIcon}>
                              <ErrorIcon style={{ fontSize: 24, color: '#F44336' }} />
                            </div>
                          ) : (
                            <div className={styles.circularProgress}>
                              <svg className={styles.progressSvg} width="32" height="32">
                                <circle
                                  className={styles.progressBackground}
                                  cx="16"
                                  cy="16"
                                  r="14"
                                />
                                <circle
                                  className={styles.progressForeground}
                                  cx="16"
                                  cy="16"
                                  r="14"
                                  style={{
                                    strokeDashoffset: 88 - (88 * (file.progress || 0)) / 100,
                                  }}
                                />
                              </svg>
                              {/* Individual pause/cancel buttons commented out - only progress bar needed */}
                              {/* {(isHovering || isPaused) && !isCompleted ? (
                                <button
                                  className={styles.actionButton}
                                  onClick={() => {
                                    if (isDownloading) {
                                      handlePauseDownload(file.id);
                                    } else if (isPaused) {
                                      handleResumeDownload(file.id);
                                    } else {
                                      handleCancelDownload(file.id);
                                    }
                                  }}
                                  type="button"
                                >
                                  {isDownloading ? (
                                    <Pause style={{ fontSize: 16, color: '#FFA726' }} />
                                  ) : isPaused ? (
                                    <PlayArrow style={{ fontSize: 16, color: '#66BB6A' }} />
                                  ) : (
                                    <Close style={{ fontSize: 16, color: '#EF5350' }} />
                                  )}
                                </button>
                              ) : ( */}
                                <span className={styles.progressText}>
                                  {Math.round(file.progress || 0)}%
                                </span>
                              {/* )} */}
                            </div>
                          )}
                        </div>
                      </div>
                      {isDownloading && (
                        <div className={styles.linearProgressContainer}>
                          <div
                            className={styles.linearProgress}
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Uploading Section */}
            {hasDownloads && uploads.length > 0 && (
              <div className={styles.sectionDivider}>
                <CloudUpload style={{ fontSize: 16, color: '#757575', marginRight: 8 }} />
                <span className={styles.sectionTitle}>Uploading</span>
              </div>
            )}
            {uploads.length === 0 && !hasDownloads ? (
              <div className={styles.emptyState}>
                <CloudUpload style={{ fontSize: 48, color: '#BDBDBD' }} />
                <div className={styles.emptyTitle}>No uploads yet</div>
                <div className={styles.emptySubtitle}>
                  Select files from the sidebar to start uploading
                </div>
              </div>
            ) : uploads.length > 0 ? (
              uploads.map((upload) => {
                const isUploading = upload.status === UPLOAD_STATUS.UPLOADING;
                const isCompleted = upload.status === UPLOAD_STATUS.COMPLETED;
                const isPaused = upload.status === UPLOAD_STATUS.PAUSED;
                const isError = upload.status === UPLOAD_STATUS.FAILED;
                const isNetworkError = upload.status === UPLOAD_STATUS.NETWORK_ERROR;
                const isInitializing = upload.status === UPLOAD_STATUS.INITIALIZING;
                const isFinishing = upload.status === UPLOAD_STATUS.FINISHING;
                const isCancelling = upload.status === UPLOAD_STATUS.CANCELLING;
                const isPending = upload.status === UPLOAD_STATUS.PENDING;
                const isHovering = hoveringItems.has(upload.id);

                return (
                  <div
                    key={upload.id}
                    className={`${styles.uploadItem} ${isError ? styles.uploadItemError : ''
                      } ${isHovering ? styles.uploadItemHover : ''}`}
                    onMouseEnter={() => handleMouseEnter(upload.id)}
                    onMouseLeave={() => handleMouseLeave(upload.id)}
                  >
                    <div className={styles.uploadItemContent}>
                      <div className={styles.fileIcon}>{getFileIcon(upload.fileName, upload.uploadType)}</div>
                      <div className={styles.uploadInfo}>
                        <div className={styles.fileName}>{upload.fileName}</div>
                        <div className={styles.uploadMeta}>
                          {isPending && (
                            <span className={styles.statusText}>Waiting in queue...</span>
                          )}
                          {isInitializing && (
                            <span className={styles.statusText}>Initializing...</span>
                          )}
                          {isFinishing && (
                            <span className={styles.statusText}>Completing upload...</span>
                          )}
                          {isCancelling && (
                            <span className={styles.statusTextWarning}>Cancelling...</span>
                          )}
                          {isPaused && (
                            <span className={styles.statusTextPaused}>
                              Paused - Click play to resume
                            </span>
                          )}
                          {isNetworkError && (
                            <span className={styles.statusTextWarning}>
                              Network disconnected - Will resume when online
                            </span>
                          )}
                          {isError && (
                            <span className={styles.statusTextError}>Upload failed</span>
                          )}
                          {!isPending && !isInitializing && !isFinishing && !isCancelling && !isPaused && !isNetworkError && !isError && (
                            <>
                              {upload.uploadType === 'photo' ? (
                                <span className={styles.metaText}>
                                  {upload.uploadedPhotos || 0} / {upload.totalPhotos || 0} photos
                                </span>
                              ) : (
                                <>
                                  <span className={styles.metaText}>
                                    {formatBytes(upload.bytesUploaded)} / {formatBytes(upload.totalBytes)}
                                  </span>
                                  {isUploading && (
                                    <>
                                      <span className={styles.metaSeparator}>•</span>
                                      <span className={styles.metaText}>
                                        {formatTime(upload.timeRemaining)}
                                      </span>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className={styles.progressIndicator}>
                        {isPending || isInitializing || isFinishing || isCancelling ? (
                          <CircularProgress size={32} thickness={2.5} style={{ color: isCancelling ? '#ff9800' : isPending ? '#bdbdbd' : '#5aa5ef99' }} />
                        ) : isCompleted ? (
                          <div className={styles.completedIcon}>
                            <CheckCircle style={{ fontSize: 24, color: '#4CAF50' }} />
                          </div>
                        ) : isError ? (
                          <div className={styles.errorIcon}>
                            <ErrorIcon style={{ fontSize: 24, color: '#F44336' }} />
                          </div>
                        ) : (
                          <div className={styles.circularProgress}>
                            <svg className={styles.progressSvg} width="32" height="32">
                              <circle
                                className={styles.progressBackground}
                                cx="16"
                                cy="16"
                                r="14"
                              />
                              <circle
                                className={styles.progressForeground}
                                cx="16"
                                cy="16"
                                r="14"
                                style={{
                                  strokeDashoffset: 88 - (88 * upload.progress) / 100,
                                }}
                              />
                            </svg>
                            {upload.uploadType === 'photo' ? (
                              // Photo uploads - show progress only (no pause/resume)
                              <span className={styles.progressText}>
                                {Math.round(upload.progress)}%
                              </span>
                            ) : (
                              // Video uploads - show pause/resume/cancel controls
                              <>
                                {(isHovering || isPaused || isNetworkError) && !isPending && !isInitializing && !isFinishing ? (
                                  <button
                                    className={styles.actionButton}
                                    onClick={() => {
                                      if (isUploading) {
                                        handlePauseResume(upload.id, upload.status);
                                      } else if (isPaused || isNetworkError) {
                                        handlePauseResume(upload.id, upload.status);
                                      } else {
                                        handleCancel(upload.id);
                                      }
                                    }}
                                    type="button"
                                  >
                                    {isUploading ? (
                                      <Pause style={{ fontSize: 16, color: '#FFA726' }} />
                                    ) : isPaused || isNetworkError ? (
                                      <PlayArrow style={{ fontSize: 16, color: '#66BB6A' }} />
                                    ) : (
                                      <Close style={{ fontSize: 16, color: '#EF5350' }} />
                                    )}
                                  </button>
                                ) : (
                                  <span className={styles.progressText}>
                                    {Math.round(upload.progress)}%
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {isUploading && (
                      <div className={styles.linearProgressContainer}>
                        <div
                          className={styles.linearProgress}
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};

export default UploadPanel;
